import { useQuery } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'
import { type BrokersPaginatedTaskList } from 'utils/web_api.schemas'

import { GrapeAPIError, type GrapeAPIErrorType } from '../GrapeAPIError'

export type TasksSingleValueFilters = {
  after_date?: string
  before_date?: string
  v2_only?: string
}

export type TasksArrayValueFilters = {
  statuses?: string[]
  execution_methods?: string[]
  handlers?: string[]
  member_ids?: string[]
  broker_ids?: string[]
  carrier_names?: string[]
  enrollment_categories?: string[]
  task_types?: string[]
  operations_batch_ids?: string[]
}

export type TasksFilters = TasksSingleValueFilters & TasksArrayValueFilters

type GetTasksQueryArgs = {
  filters: TasksFilters
  pagination: PaginationState
  sorting: SortingState
}

export function getTasksQueryKey ({
  filters,
  pagination,
  sorting,
}: GetTasksQueryArgs) {
  return [
    'brokers',
    'tasks',
    'filters',
    filters,
    'pagination',
    pagination,
    'sorting',
    sorting,
  ]
}

// TODO: Figure out how to setup Grape Endpoint so generated types can match the filters state.
export function useGetTasks ({
  filters,
  pagination,
  sorting,
}: GetTasksQueryArgs) {
  return useQuery<BrokersPaginatedTaskList, GrapeAPIErrorType>({
    queryKey: getTasksQueryKey({ filters, pagination, sorting }),
    queryFn: async () => {
      const searchParams = new URLSearchParams()

      searchParams.set('page', pagination.pageIndex.toString())
      searchParams.set('items', pagination.pageSize.toString())

      if (sorting.length > 0) {
        const { id, desc } = sorting[0]
        searchParams.set(`sort[${id}]`, desc ? 'desc' : 'asc')
      }

      Object.entries(filters).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((v) => searchParams.append(`filters[${key}][]`, v))
        } else if (val) {
          searchParams.append(`filters[${key}]`, val.toString())
        }
      })

      const response = await fetchWithHeaders(
        `/api/web/brokers/tasks?${searchParams.toString()}`
      )
      if (!response.ok) {
        const errorJson = await response.json()
        throw GrapeAPIError({
          status: response.status,
          message: errorJson.message,
          errors: errorJson.errors,
        })
      }

      return response.json()
    },
  })
}
